import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslate } from 'sbx-crm-menu-ui';

interface Props {
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    totalPages: number;
    rowCount: number;
}

const buttonClassName = '!bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full p-3 hover:from-purple-600 hover:to-pink-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-opacity-50 disabled:cursor-not-allowed';

export const PaginationV2 = ({ page, setPage, totalPages, rowCount }: Props) => {
    const { t } = useTranslate('common');
    useEffect(() => {
        setPage(1);
    }, [totalPages]);

    const pageRange = useMemo(() => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        } else {
            const rangeStart = Math.max(1, page - 3);
            const rangeEnd = Math.min(totalPages, rangeStart + 6);
            return Array.from({ length: rangeEnd - rangeStart + 1 }, (_, i) => i + rangeStart);
        }
    }, [page, totalPages]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-center items-center gap-2 my-8"
        >
            {
                totalPages > 7 && page !== 1 && (
                    <div>
                        <button
                            onClick={() => setPage(1)}
                            type="button"
                            className={buttonClassName}
                        >First page
                        </button>
                    </div>
                )
            }

            <div className="flex justify-center gap-1">
                <button
                    disabled={page === 1}
                    className={buttonClassName}
                    onClick={() => setPage(page - 1)}
                    type="button"
                >
                    <svg
                        aria-hidden="true"
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clipRule="evenodd"
                        ></path>
                    </svg>
                </button>

                <div className={'flex items-center '}>
                    {
                        pageRange.map(num => (
                            <button
                                key={num}
                                onClick={() => setPage(num)}
                                type="button"
                                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                                    page === num
                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold'
                                        : 'text-gray-600 hover:bg-purple-100'
                                }`}
                            >{num}</button>
                        ))
                    }
                </div>

                <button
                    disabled={page === totalPages}
                    className={buttonClassName}
                    onClick={() => setPage(page + 1)}
                    type="button"
                >
                    <svg
                        aria-hidden="true"
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                        ></path>
                    </svg>
                </button>
            </div>

            {
                totalPages > 7 && page !== totalPages && (
                    <div>
                        <button
                            onClick={() => setPage(totalPages)}
                            className={buttonClassName}
                            type="button"
                        >Last page
                        </button>
                    </div>
                )
            }

            <div>
                {rowCount} {t(rowCount > 1 ? 'rows' : 'row')}
            </div>
        </motion.div>
    );
};
